(function () {
  'use strict';

  var LS_KEY         = 'go_group_id';
  var LS_NAME_KEY    = 'go_cart_name';
  var LS_PENDING_KEY = 'go_checkout_pending';
  var TABLE          = 'shared_carts';
  var POLL_MS        = 3000;
  var PUSH_DELAY     = 1200;
  var SESSION_TTL_MS = 48 * 60 * 60 * 1000; // 48 hours

  var state = {
    supabase:          null,
    groupId:           null,
    cartName:          '',
    channel:           null,
    sessionActive:     false,
    syncing:           false,
    lastUpdatedAt:     null,
    pollTimer:         null,
    pushTimer:         null,
    config:            {},
    lastSyncedItems:   null,   // items we last pulled from remote — skip push if cart matches
    pendingRemoteRow:  null,   // latest remote update queued while a sync was running
    listenersAttached: false
  };

  // ─── INIT ──────────────────────────────────────────────────────────────────
  function init() {
    var configEl = document.getElementById('group-order-config');

    state.config = {
      supabaseUrl:       'https://pfxqkxjzduofyzzuolal.supabase.co',
      supabaseKey:       'sb_publishable_rNrQ_LaxJbErsWbfXl0NgQ_Q3_2puN6',
      freeShipThreshold: 4000
    };

    // Allow theme settings to override hardcoded values if present
    if (configEl) {
      if (configEl.dataset.supabaseUrl)           state.config.supabaseUrl       = configEl.dataset.supabaseUrl;
      if (configEl.dataset.supabaseKey)           state.config.supabaseKey       = configEl.dataset.supabaseKey;
      if (configEl.dataset.freeShippingThreshold) state.config.freeShipThreshold = parseInt(configEl.dataset.freeShippingThreshold, 10);
    }

    if (!state.config.supabaseUrl || !state.config.supabaseKey) return;

    state.supabase = window.supabase.createClient(
      state.config.supabaseUrl,
      state.config.supabaseKey
    );

    attachButtonListeners();
    attachCheckoutInterceptor();
    interceptCartFetch();
    checkPendingCheckout();
    checkUrlForGroupId();
  }

  // ─── CHECK PENDING CHECKOUT ────────────────────────────────────────────────
  // If the user returns from checkout with an empty cart, the order was placed.
  function checkPendingCheckout() {
    var pendingId = null;
    try { pendingId = localStorage.getItem(LS_PENDING_KEY); } catch (e) {}
    if (!pendingId) return;
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        try { localStorage.removeItem(LS_PENDING_KEY); } catch (e) {}
        if (cart.item_count === 0) markOrderPlaced(pendingId);
      })
      .catch(function () {
        try { localStorage.removeItem(LS_PENDING_KEY); } catch (e) {}
      });
  }

  function markOrderPlaced(groupId) {
    state.supabase
      .from(TABLE)
      .update({ order_placed: true, updated_at: new Date().toISOString() })
      .eq('id', groupId)
      .then(function () {});
    clearLocalSession();
    showToast('Group order placed! Start a new shared cart to shop together again.', 'success');
  }

  // ─── URL PARAM LOADER ──────────────────────────────────────────────────────
  function checkUrlForGroupId() {
    var params  = new URLSearchParams(window.location.search);
    var groupId = params.get('group_id');
    if (groupId) {
      state.groupId = groupId;
      try { localStorage.setItem(LS_KEY, groupId); } catch (e) {}
      loadAndSyncRemoteCart(groupId);
    } else {
      var stored = null, storedName = '';
      try {
        stored     = localStorage.getItem(LS_KEY);
        storedName = localStorage.getItem(LS_NAME_KEY) || '';
      } catch (e) {}
      if (stored) {
        // Verify the session still exists and hasn't expired before restoring
        fetchRemoteCart(stored)
          .then(function (row) {
            if (!row || row.order_placed || isSessionExpired(row)) {
              clearLocalSession();
              return;
            }
            var name = row.name || storedName;
            try { localStorage.setItem(LS_NAME_KEY, name); } catch (e) {}
            state.groupId       = stored;
            state.cartName      = name;
            state.sessionActive = true;
            state.lastUpdatedAt = row.updated_at;
            applyCartLabelDOM(stored, name);
            startRealtimeOrPoll(stored);
          })
          .catch(function () {
            // Network failure — restore optimistically; polling will detect expiry
            state.groupId       = stored;
            state.cartName      = storedName;
            state.sessionActive = true;
            applyCartLabelDOM(stored, storedName);
            startRealtimeOrPoll(stored);
          });
      }
    }
  }

  // ─── SUPABASE READ ─────────────────────────────────────────────────────────
  function fetchRemoteCart(groupId) {
    return state.supabase
      .from(TABLE)
      .select('id, cart_json, name, expires_at, order_placed, updated_at')
      .eq('id', groupId)
      .single()
      .then(function (result) {
        if (result.error) return null;
        return result.data;
      });
  }

  // ─── SUPABASE WRITE ────────────────────────────────────────────────────────
  function saveCartToSupabase(cartJson, name, groupId) {
    var now     = new Date();
    var id      = groupId || crypto.randomUUID();
    var ts      = now.toISOString();
    var payload = {
      id:         id,
      cart_json:  cartJson,
      name:       name || 'Shared Cart',
      updated_at: ts
    };
    // expires_at and order_placed only set on INSERT — never overwrite on updates
    if (!groupId) {
      payload.expires_at   = new Date(now.getTime() + SESSION_TTL_MS).toISOString();
      payload.order_placed = false;
    }
    return state.supabase
      .from(TABLE)
      .upsert(payload, { onConflict: 'id' })
      .then(function (result) {
        if (result.error) {
          console.error('[group-order] Supabase upsert error:', result.error);
          throw result.error;
        }
        state.lastUpdatedAt = ts;
        return id;
      });
  }

  // ─── LOCAL CART OPS ────────────────────────────────────────────────────────
  function getLocalCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function getFreeGiftVariantIds() {
    return [];
  }

  function isFreeGiftItem(item) {
    if (!item) return false;
    var properties = item.properties || {};
    var giftType = String(properties._free_gift || '');
    var giftRule = String(properties._free_gift_rule || '');

    return (giftType === 'gated' && giftRule === 'legacy_gift') ||
      (giftType === 'mask' && (giftRule === 'cart_threshold_gift' || giftRule === '30_eur_mask')) ||
      (giftType === 'always' && giftRule === 'always_gift');
  }

  // Clears local cart and replaces with remote items.
  // Strips freegift-managed variants so freegift.js re-adds them via cart-update.
  // Merges duplicate variant_ids by summing qty.
  // Saves current cart for rollback if add fails.
  // Falls back to one-by-one adds if batch 422 (OOS items in batch).
  function syncLocalCartFromRemote(remoteItems) {
    state.syncing = true;
    clearTimeout(state.pushTimer); // cancel any queued push — it will push stale data mid-sync
    var filtered  = remoteItems.filter(function (item) {
      return !isFreeGiftItem(item);
    });

    var merged = {};
    filtered.forEach(function (item) {
      var vid = String(item.variant_id || item.id);
      if (merged[vid]) {
        merged[vid].quantity += (item.quantity || 1);
      } else {
        merged[vid] = { id: vid, quantity: item.quantity || 1, properties: item.properties || {} };
      }
    });
    var addItems = Object.values(merged);

    // Snapshot current cart for rollback
    var rollbackItems = [];
    return getLocalCart()
      .then(function (localCart) {
        rollbackItems = (localCart.items || []).map(function (i) {
          return { id: String(i.variant_id), quantity: i.quantity, properties: i.properties || {} };
        });
        return fetch('/cart/clear.js', { method: 'POST' });
      })
      .then(function () {
        if (!addItems.length) return fetch('/cart.js').then(function (r) { return r.json(); });
        return fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: addItems })
        })
        .then(function (r) {
          if (r.ok) return fetch('/cart.js').then(function (r2) { return r2.json(); });
          // 422 = one or more items OOS — fall back to individual adds, skip OOS
          return addItemsOneByOne(addItems)
            .then(function () { return fetch('/cart.js').then(function (r2) { return r2.json(); }); });
        });
      })
      .then(function (cart) {
        // Record what we synced so schedulePush can skip pushing it right back
        state.lastSyncedItems = addItems;
        state.syncing = false;
        // Drain any remote update that arrived while we were syncing — apply the
        // latest one now so we don't end up stuck on a stale cart state.
        var pending = state.pendingRemoteRow;
        state.pendingRemoteRow = null;
        document.dispatchEvent(new CustomEvent('cart-update', { bubbles: true, detail: cart }));
        if (pending) setTimeout(function () { applyRemoteRow(pending.row, pending.toastMsg); }, 0);
        return cart;
      })
      .catch(function (err) {
        state.syncing = false;
        // Attempt to restore the cart to its pre-sync state
        rollbackCart(rollbackItems);
        throw err;
      });
  }

  // Add items in parallel; silently skips any that fail (OOS, 422, network error).
  // Parallel is safe here — we're populating a fresh empty cart, so no ordering conflicts.
  function addItemsOneByOne(items) {
    return Promise.all(items.map(function (item) {
      return fetch('/cart/add.js', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ items: [item] })
      })
      .then(function (r) { return r.text().catch(function () {}); })
      .catch(function () {});
    }));
  }

  // Restore cart to a previous state after a failed sync
  function rollbackCart(rollbackItems) {
    if (!rollbackItems || !rollbackItems.length) return Promise.resolve();
    return fetch('/cart/clear.js', { method: 'POST' })
      .then(function () {
        return fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ items: rollbackItems })
        });
      })
      .catch(function () {/* best-effort, ignore rollback failure */});
  }

  // ─── PUSH LOCAL CART TO SUPABASE ───────────────────────────────────────────
  function schedulePush() {
    if (!state.sessionActive || !state.groupId || state.syncing) return;
    clearTimeout(state.pushTimer);
    state.pushTimer = setTimeout(function () {
      executePush(0);
    }, PUSH_DELAY);
  }

  function executePush(attempt) {
    if (!state.sessionActive || !state.groupId || state.syncing) return;
    var targetId = state.groupId; // capture in case session ends mid-flight
    getLocalCart()
      .then(function (cart) {
        // Re-check after async getLocalCart — a remote sync may have started
        if (!state.sessionActive || !state.groupId || state.syncing) return;
        var items = (cart.items || []).filter(function (item) {
          return !isFreeGiftItem(item);
        });
        // Skip if cart matches what we last received from remote — prevents push-back loop
        if (state.lastSyncedItems !== null && !cartsDiffer(items, state.lastSyncedItems)) return;
        return saveCartToSupabase(JSON.stringify({ items: items }), state.cartName, targetId);
      })
      .catch(function (err) {
        if (attempt < 3) {
          // Exponential backoff: 1s, 2s, 4s
          setTimeout(function () { executePush(attempt + 1); }, Math.pow(2, attempt) * 1000);
        } else {
          console.error('[group-order] Push failed after 3 retries:', err);
        }
      });
  }

  // ─── SHALLOW DIFF ──────────────────────────────────────────────────────────
  // Ignores freegift-managed variants so gift additions never trigger a sync loop.
  function cartsDiffer(remoteItems, localItems) {
    var rFiltered = remoteItems.filter(function (i) {
      return !isFreeGiftItem(i);
    });
    var lFiltered = localItems.filter(function (i) {
      return !isFreeGiftItem(i);
    });
    if (rFiltered.length !== lFiltered.length) return true;
    var remoteMap = {};
    rFiltered.forEach(function (i) { remoteMap[String(i.variant_id || i.id)] = i.quantity; });
    return lFiltered.some(function (i) {
      return remoteMap[String(i.variant_id || i.id)] !== i.quantity;
    });
  }

  // ─── EXPIRY / ORDER PLACED ─────────────────────────────────────────────────
  function isSessionExpired(row) {
    if (!row.expires_at) return false;
    return new Date(row.expires_at) < new Date();
  }

  function handleSessionOrderPlaced() {
    stopPolling();
    if (state.channel) { state.supabase.removeChannel(state.channel); state.channel = null; }
    clearLocalSession();
    showToast('This group cart has been ordered. Start a new shared cart to shop together again.', 'info');
  }

  function clearLocalSession() {
    try { localStorage.removeItem(LS_KEY); localStorage.removeItem(LS_NAME_KEY); } catch (e) {}
    state.groupId        = null;
    state.cartName       = '';
    state.sessionActive  = false;
    state.lastSyncedItems = null;
    state.lastUpdatedAt  = null;
  }

  // ─── SHARED REMOTE-ROW HANDLER ────────────────────────────────────────────
  // Used by both polling and realtime. Parses cart_json, diffs against local cart,
  // and syncs if needed.
  // Guards against concurrent syncs in three ways:
  //   1. If a sync is already running, queue this row as pendingRemoteRow (always
  //      keeping the latest) — it will be replayed after the sync completes.
  //   2. After the async getLocalCart() gap, a stale-check (capturedUpdatedAt)
  //      ensures we only apply the newest update when rapid events arrive.
  //   3. A second syncing guard inside the getLocalCart callback handles the case
  //      where a sync started between the outer check and the async resolution.
  function applyRemoteRow(row, toastMsg) {
    if (!row || !row.cart_json) return;
    if (row.order_placed) { handleSessionOrderPlaced(); return; }
    if (row.updated_at && row.updated_at === state.lastUpdatedAt) return;
    if (state.syncing) {
      // Don't drop this update — queue it so it's applied once the current sync
      // finishes. Always keep the latest (overwrite any previously queued row).
      state.pendingRemoteRow = { row: row, toastMsg: toastMsg };
      return;
    }
    state.lastUpdatedAt = row.updated_at; // lock early — prevents double-processing
    var capturedUpdatedAt = row.updated_at; // snapshot for stale-check after async gap
    var remoteItems;
    try {
      var parsed = typeof row.cart_json === 'string' ? JSON.parse(row.cart_json) : row.cart_json;
      remoteItems = parsed.items || [];
    } catch (e) { return; }
    getLocalCart().then(function (localCart) {
      if (state.syncing) return;
      // A newer remote event arrived while getLocalCart() was in flight — skip
      // this stale update; the newer one will be (or already is) processing.
      if (state.lastUpdatedAt !== capturedUpdatedAt) return;
      if (!cartsDiffer(remoteItems, localCart.items || [])) return;
      syncLocalCartFromRemote(remoteItems).then(function () {
        showToast(toastMsg, 'info');
      });
    });
  }

  // ─── POLLING FALLBACK ──────────────────────────────────────────────────────
  function startPolling(groupId) {
    stopPolling();
    state.pollTimer = setInterval(function () {
      if (state.syncing) return;
      fetchRemoteCart(groupId).then(function (row) {
        if (!row) return;
        if (isSessionExpired(row)) {
          clearLocalSession();
          stopPolling();
          showToast('Your shared cart has expired. Create a new one to continue.', 'info');
          return;
        }
        applyRemoteRow(row, 'Group cart updated.');
      });
    }, POLL_MS);
  }

  function stopPolling() {
    if (state.pollTimer) { clearInterval(state.pollTimer); state.pollTimer = null; }
  }

  // ─── REALTIME + POLLING SETUP ──────────────────────────────────────────────
  // Tries Realtime first; falls back to polling after 5s if not subscribed.
  function startRealtimeOrPoll(groupId) {
    if (state.channel) state.supabase.removeChannel(state.channel);
    var realtimeOk    = false;
    var fallbackTimer = setTimeout(function () {
      if (!realtimeOk) startPolling(groupId);
    }, 5000);

    state.channel = state.supabase
      .channel('shared_carts:' + groupId)
      .on('postgres_changes',
        { event: 'UPDATE', schema: 'public', table: TABLE, filter: 'id=eq.' + groupId },
        function (payload) { applyRemoteRow(payload.new, 'Group cart updated by another member.'); }
      )
      .subscribe(function (status) {
        if (status === 'SUBSCRIBED') {
          realtimeOk = true;
          clearTimeout(fallbackTimer);
          stopPolling();
        }
      });
  }

  // ─── LOAD + SYNC FLOW ──────────────────────────────────────────────────────
  function loadAndSyncRemoteCart(groupId) {
    showToast('Loading group cart\u2026', 'info');
    fetchRemoteCart(groupId)
      .then(function (row) {
        if (!row || !row.cart_json) {
          showToast('Group session not found.', 'error');
          try { localStorage.removeItem(LS_KEY); } catch (e) {}
          return;
        }
        if (row.order_placed) {
          showToast('This group cart has already been ordered. Ask the group to start a new one.', 'info');
          return;
        }
        if (isSessionExpired(row)) {
          showToast('This shared cart link has expired. Ask the group to create a new one.', 'info');
          return;
        }

        state.lastUpdatedAt = row.updated_at;
        var cartName = row.name || 'Shared Cart';
        state.cartName = cartName;
        try { localStorage.setItem(LS_NAME_KEY, cartName); } catch (e) {}

        var remoteItems;
        try {
          var parsed = typeof row.cart_json === 'string' ? JSON.parse(row.cart_json) : row.cart_json;
          remoteItems = parsed.items || [];
        } catch (e) {
          showToast('Invalid group cart data.', 'error');
          return;
        }

        return getLocalCart()
          .then(function (localCart) {
            if (cartsDiffer(remoteItems, localCart.items || [])) {
              return syncLocalCartFromRemote(remoteItems);
            }
          })
          .then(function () {
            // The cart page is server-rendered at load time, so it shows an empty
            // cart even after syncLocalCartFromRemote has updated Shopify's cart via
            // API. Reloading (stripping ?group_id= from the URL) causes Shopify to
            // re-render the page with the now-populated cart. The session is already
            // persisted in localStorage so checkUrlForGroupId's else-branch restores
            // it cleanly on the next load without re-triggering this sync path.
            if (new URLSearchParams(window.location.search).has('group_id')) {
              window.location.replace(window.location.pathname);
              return;
            }
            showToast('\u201c' + cartName + '\u201d loaded.', 'success');
            state.sessionActive = true;
            applyCartLabelDOM(groupId, cartName);
            startRealtimeOrPoll(groupId);
          });
      })
      .catch(function () {
        showToast('Error loading group session.', 'error');
      });
  }

  // ─── SHARE MODAL ───────────────────────────────────────────────────────────
  function openShareModal() {
    var modal = document.getElementById('go-name-modal');
    var input = document.getElementById('go-cart-name-input');
    if (!modal) return;
    modal.removeAttribute('hidden');
    if (input) {
      input.value = '';
      input.classList.remove('go-modal__input--error');
      setTimeout(function () { input.focus(); }, 50);
    }
  }

  function closeShareModal() {
    var modal = document.getElementById('go-name-modal');
    if (modal) modal.setAttribute('hidden', '');
  }

  function confirmShareModal() {
    var input = document.getElementById('go-cart-name-input');
    var name  = input ? input.value.trim() : '';
    if (!name) {
      if (input) { input.focus(); input.classList.add('go-modal__input--error'); }
      return;
    }
    if (name.split(/\s+/).length > 5) {
      if (input) {
        input.focus();
        input.classList.add('go-modal__input--error');
        var hint = document.getElementById('go-name-hint');
        if (!hint) {
          hint = document.createElement('p');
          hint.id = 'go-name-hint';
          hint.style.cssText = 'color:#c0392b;font-size:12px;margin:-10px 0 10px;';
          input.insertAdjacentElement('afterend', hint);
        }
        hint.textContent = 'Please use 5 words or fewer.';
      }
      return;
    }
    // Clear any previous hint
    var hint = document.getElementById('go-name-hint');
    if (hint) hint.remove();
    closeShareModal();
    handleStartSession(name);
  }

  // ─── START SESSION ─────────────────────────────────────────────────────────
  function handleStartSession(name) {
    getLocalCart()
      .then(function (localCart) {
        // Strip gift items from initial save so joiners don't receive freegift variants
        var items = (localCart.items || []).filter(function (item) {
          return !isFreeGiftItem(item);
        });
        return saveCartToSupabase(JSON.stringify({ items: items }), name, null);
      })
      .then(function (newId) {
        state.groupId      = newId;
        state.cartName     = name;
        state.sessionActive = true;
        try { localStorage.setItem(LS_KEY, newId); localStorage.setItem(LS_NAME_KEY, name); } catch (e) {}
        var shareUrl = buildShareUrl(newId);
        shareOrCopy(shareUrl);
        showToast('\u201c' + name + '\u201d shared! Link copied.', 'success');
        applyCartLabelDOM(newId, name);
        startRealtimeOrPoll(newId);
      })
      .catch(function (err) {
        console.error('[group-order] handleStartSession failed:', err);
        showToast('Could not start group order.', 'error');
      });
  }

  // ─── SHARE URL ─────────────────────────────────────────────────────────────
  function buildShareUrl(groupId) {
    // Always use the cart page as the landing URL — avoids leaking current page
    // filters, search params, or product variant state into the share link
    return window.location.origin + '/cart?group_id=' + encodeURIComponent(groupId);
  }

  function shareOrCopy(url) {
    if (navigator.share) {
      navigator.share({ url: url, title: state.cartName || 'Join my group order' })
        .catch(function () { copyToClipboard(url); });
    } else {
      copyToClipboard(url);
    }
  }

  function copyToClipboard(text) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).catch(function () { fallbackCopy(text); });
    } else {
      fallbackCopy(text);
    }
  }

  function fallbackCopy(text) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); } catch (e) {}
    document.body.removeChild(ta);
  }

  // ─── SESSION BANNER ────────────────────────────────────────────────────────
  // Inject cart name label into cart drawer header + cart page heading.
  // Safe to call multiple times — checks for existing label before re-injecting.
  // Truncate a string to at most maxWords words; appends "..." if truncated.
  function truncateWords(str, maxWords) {
    var words = str.trim().split(/\s+/);
    if (words.length <= maxWords) return str.trim();
    return words.slice(0, maxWords).join(' ') + '...';
  }

  function applyCartLabelDOM(groupId, cartName) {
    var raw      = cartName || 'Shared Cart';
    var truncated = truncateWords(raw, 5);
    var name     = escHtml(truncated);
    var labelHtml =
      '<span class="go-cart-label">' + name + '\u2019s</span>' +
      ' Shopping Cart ' +
      '<button class="go-label-copy" data-go-label-copy aria-label="Copy share link" title="Copy share link">' +
        '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>' +
      '</button>';

    // Cart drawer title — target specifically inside .cart-sidebar-main to avoid
    // hitting other .halo-sidebar-header .title elements (collection sidebars etc.)
    var drawerTitle = document.querySelector('.cart-sidebar-main .halo-sidebar-header .title');
    if (drawerTitle) {
      drawerTitle.innerHTML = labelHtml;
    }

    // Cart page heading
    var pageTitle = document.querySelector('h1.page-header');
    if (pageTitle && !pageTitle.querySelector('.go-cart-label')) {
      pageTitle.innerHTML = labelHtml;
    }

    // Bind copy button(s) — use event delegation so it works after re-renders
    document.querySelectorAll('[data-go-label-copy]').forEach(function (btn) {
      if (!btn.dataset.goBound) {
        btn.dataset.goBound = '1';
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          copyToClipboard(buildShareUrl(groupId));
          showToast('Link copied!', 'success');
        });
      }
    });
  }

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }


  // ─── CHECKOUT INTERCEPTOR ──────────────────────────────────────────────────
  // freegift.js fires first (non-deferred), validates gifts, re-fires click with
  // allowCheckoutPassthrough. That second click bypasses freegift and hits this.
  function attachCheckoutInterceptor() {
    document.addEventListener('click', function (e) {
      if (!state.sessionActive) return;
      var btn = e.target.closest('.button-checkout, #cart-checkout, #cart-sidebar-checkout');
      if (!btn) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      btn.disabled = true;
      showToast('Checking stock\u2026', 'info');
      // Mark pending so checkPendingCheckout detects a completed order on next load
      try { localStorage.setItem(LS_PENDING_KEY, state.groupId); } catch (e) {}
      getLocalCart()
        .then(function (cart) { return validateStock(cart.items || []); })
        .then(function (oos) {
          btn.disabled = false;
          if (oos.length === 0) {
            window.location = btn.dataset.href || '/checkout';
          } else {
            try { localStorage.removeItem(LS_PENDING_KEY); } catch (e) {}
            showToast('Out of stock: ' + oos.join(', '), 'error');
          }
        })
        .catch(function () {
          btn.disabled = false;
          window.location = btn.dataset.href || '/checkout';
        });
    }, true);
  }

  // ─── STOCK VALIDATION ──────────────────────────────────────────────────────
  function validateStock(items) {
    var checks = items.map(function (item) {
      if (!item.handle) return Promise.resolve(null);
      return fetch('/products/' + item.handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (product) {
          var variant = (product.variants || []).find(function (v) { return v.id === item.variant_id; });
          if (!variant) return null;
          return variant.available ? null : (item.product_title || item.title || item.handle);
        })
        .catch(function () { return null; });
    });
    return Promise.all(checks).then(function (r) { return r.filter(Boolean); });
  }

  // ─── TOAST ─────────────────────────────────────────────────────────────────
  function showToast(message, type) {
    var container = document.getElementById('go-toast-container');
    if (!container) return;
    var toast = document.createElement('div');
    toast.className = 'go-toast go-toast--' + (type || 'info');
    toast.setAttribute('role', 'alert');
    toast.textContent = message;
    container.appendChild(toast);
    requestAnimationFrame(function () { toast.classList.add('go-toast--visible'); });
    setTimeout(function () {
      toast.classList.remove('go-toast--visible');
      setTimeout(function () { if (toast.parentNode) toast.parentNode.removeChild(toast); }, 300);
    }, 4000);
  }

  // ─── FETCH INTERCEPTOR ─────────────────────────────────────────────────────
  // Theme qty +/- buttons call /cart/change.js or /cart/update.js directly
  // without dispatching cart-update. Wrapping fetch ensures we always push
  // after any cart-modifying request, regardless of what the theme does.
  // Handles both string URLs and Request objects; only pushes on 2xx responses.
  function interceptCartFetch() {
    var cartMutate = /\/cart\/(add|change|update)\.js/;
    var origFetch  = window.fetch;
    window.fetch = function (resource) {
      var url = (resource instanceof Request)
        ? resource.url
        : (typeof resource === 'string' ? resource : '');
      var result = origFetch.apply(this, arguments);
      if (cartMutate.test(url) && !state.syncing) {
        result.then(function (r) {
          if (r && r.ok) schedulePush();
        }).catch(function () {});
      }
      return result;
    };
  }

  // ─── BUTTON LISTENERS ──────────────────────────────────────────────────────
  function attachButtonListeners() {
    if (state.listenersAttached) return;
    state.listenersAttached = true;
    document.addEventListener('click', function (e) {
      // "Share cart" hint link
      if (e.target.closest('[data-go-open-share-modal]')) {
        if (state.sessionActive && state.groupId) {
          // Already in a session — just copy the link
          copyToClipboard(buildShareUrl(state.groupId));
          showToast('Link copied!', 'success');
        } else {
          openShareModal();
        }
        return;
      }
      // Modal confirm / cancel / backdrop
      if (e.target.closest('#go-modal-confirm'))  { confirmShareModal(); return; }
      if (e.target.closest('#go-modal-cancel') ||
          e.target.id === 'go-modal-backdrop')     { closeShareModal();   return; }
    });

    // Keyboard: Enter confirms, Escape closes
    document.addEventListener('keydown', function (e) {
      var modal = document.getElementById('go-name-modal');
      if (!modal || modal.hidden) return;
      if (e.key === 'Enter')  confirmShareModal();
      if (e.key === 'Escape') closeShareModal();
    });

    // cart-update: re-apply label after sidebar AJAX reload + update progress bar.
    // Do NOT call schedulePush() here — interceptCartFetch already schedules the
    // push for every user-initiated mutation. Calling it here too causes a
    // push-back loop when the event is fired after a remote→local sync.
    document.addEventListener('cart-update', function () {
      if (!state.sessionActive) return;
      // Re-apply label after sidebar AJAX reload clears the DOM
      setTimeout(function () {
        if (state.sessionActive && state.groupId) applyCartLabelDOM(state.groupId, state.cartName);
      }, 400);
    });

    // Re-apply after theme's drawer loaded event
    ['theme.cart.drawer.loaded', 'halo.cart.sidebar.loaded'].forEach(function (evName) {
      document.addEventListener(evName, function () {
        if (state.sessionActive && state.groupId) {
          setTimeout(function () { applyCartLabelDOM(state.groupId, state.cartName); }, 50);
        }
      });
    });
  }

  // ─── BOOTSTRAP ─────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
